import {useCallback} from 'react'

const FOCUS_VISIBLE_ATTR = 'data-focus-visible-within'
const ROW_SELECTOR = 'li'

/**
 * Returns a callback ref for a list container that marks the closest row
 * (`<li>`) with `data-focus-visible-within` while it contains a
 * `:focus-visible` descendant.
 *
 * This replaces a `:has(:focus-visible)` selector. On long, frequently
 * re-rendered lists, `:has()` forces WebKit into expensive sideways/outward
 * style invalidation (effectively O(N²) across the list), whereas a single
 * delegated focusin/focusout listener invalidates only the affected row.
 */
export function useFocusVisibleWithin() {
  return useCallback((node: HTMLElement | null) => {
    if (!node) return

    const onFocusIn = (event: FocusEvent) => {
      const target = event.target
      if (!(target instanceof HTMLElement)) return
      if (!target.matches(':focus-visible')) return
      const row = target.closest<HTMLElement>(ROW_SELECTOR)
      if (row && node.contains(row)) {
        row.setAttribute(FOCUS_VISIBLE_ATTR, 'true')
      }
    }

    const onFocusOut = (event: FocusEvent) => {
      const target = event.target
      if (!(target instanceof HTMLElement)) return
      const row = target.closest<HTMLElement>(ROW_SELECTOR)
      if (!row) return
      const nextFocused = event.relatedTarget
      if (nextFocused instanceof Node && row.contains(nextFocused)) return
      row.removeAttribute(FOCUS_VISIBLE_ATTR)
    }

    node.addEventListener('focusin', onFocusIn)
    node.addEventListener('focusout', onFocusOut)

    return () => {
      node.removeEventListener('focusin', onFocusIn)
      node.removeEventListener('focusout', onFocusOut)
    }
  }, [])
}
